import { Modal } from "./modal"
import { GraphQLClient, gql } from 'graphql-request';
import { store } from './react-initialize';
import * as Sentry from '@sentry/browser';
import { setErrorSampleRate, resetErrorSampleRate } from "./error_capture";

/* Translators: %1 is an email address */
const CHECKOUT_UNAVAILABLE_MESSAGE = _js(
   'Checkout unavailable. Please contact %1.',
   'support@ifixit.com'
);
/* Translators: %1 -> link; %2 -> link; */
const INVALID_EMAIL_MESSAGE = _js(
   "We're having a problem communicating with your account's email address. Please contact our Pro \
    Wholesale support team at %1 or %2 so we can reapply your discount.",
   '<a href="mailto:pro@ifixit.com">pro@ifixit.com</a>',
   '<a href="tel:8055406339">(805) 540-6339</a>'
);
/* Translators: %1 is an email address */
const ITEM_PROBLEMS_PREFIX = _js(
   'Checkout error. Please contact %1. Products: ',
   'support@ifixit.com'
);
const OUTDATED_CART_MESSAGE = _js('Your cart is out of date. Please refresh the page.');

const INVALID_EMAIL = 'Invalid email';
const OUTDATED_CART = 'Outdated cart';

export default function checkoutOnShopify() {
   const products = store.getState().headerState.miniCart.products;
   // The user is trying to check out, let's capture *all* the errors that
   // happen during this important time
   setErrorSampleRate(1);

   let result;
   if (App.shopifyCheckout.useDraftOrder) {
      result = checkoutWithDraftOrder(products);
   } else {
      result = createNormalCheckout(products);
   }
   showLoadingIndicator(result);
   result.finally(resetErrorSampleRate);
}

function checkoutWithDraftOrder(products) {
   const possiblyRejectedPromise = validateProductsExistOnShopify(products);
   if (possiblyRejectedPromise) {
      return possiblyRejectedPromise;
   }

   return new Request.API_2_0('cart/order/draftOrder', {
      method: 'POST',
      onSuccess: function (response) {
         redirectWithSSO(response.invoiceUrl);
      },
      onFailure: function (xhr, response) {
         if (response !== INVALID_EMAIL) {
            Sentry.captureMessage('Shopify Pro Draft Order Checkout API Failure', {
               extra: {
                  'Products in Cart': products,
                  Response: response,
                  XMLHttpRequest: xhr,
               },
            });
         }
         console.error(new Error(response));
      },
   })
      .send()
      .catch(e => {
         if (e === INVALID_EMAIL) {
            Modal.alert(INVALID_EMAIL_MESSAGE);
            return Promise.resolve();
         } else {
            return Promise.reject({
               failure: true,
               message: e === OUTDATED_CART ? OUTDATED_CART_MESSAGE : CHECKOUT_UNAVAILABLE_MESSAGE,
            });
         }
      });
}

function createNormalCheckout(products) {
   const version = App.shopifyCheckout.clientDetails.apiVersion;
   const url = `https://${App.shopifyCheckout.clientDetails.domain}/api/${version}/graphql.json`;
   const headers = {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': App.shopifyCheckout.clientDetails.storefrontAccessToken
   };
   const graphQLClient = new GraphQLClient(url, { headers });

   const createCheckoutMutation = gql`
      mutation ($input: CheckoutCreateInput!) {
         checkoutCreate(input: $input) {
            checkout {
               webUrl
            }
            checkoutUserErrors {
               field
               message
               code
            }
         }
      }
   `;

   const checkoutLineItems = {
      lineItems: products.map(product => ({
         variantId: product.variantId,
         quantity: product.quantity,
      })),
   };
   const variables = {input: checkoutLineItems};

   return graphQLClient.request(createCheckoutMutation, variables).then((response) => {
      const { checkout, checkoutUserErrors } = response.checkoutCreate;
      if (checkoutUserErrors.length) {
         console.error(checkoutUserErrors);
         Sentry.captureMessage('Checkout User Errors', {
            extra: {
               createCheckoutMutation,
               variables,
               checkoutUserErrors,
            },
         });
         const userError = new Error(CHECKOUT_UNAVAILABLE_MESSAGE);
         console.error(userError);
         throw userError;
      } else {
         const checkoutUrl = new URL(checkout.webUrl);
         addUtmTrackerParam(checkoutUrl);
         const ssoUrl = new URL(App.shopifyCheckout.ssoRoute);
         ssoUrl.searchParams.set('return_to', checkoutUrl);

         window.location = App.isLoggedIn ? ssoUrl : checkoutUrl;
      }
   }).catch((error) => {
      console.error(error);
      Sentry.captureException(error, {
         extra: {
            createCheckoutMutation,
            variables,
         },
      });
      const userError = new Error(CHECKOUT_UNAVAILABLE_MESSAGE);
      console.error(userError);
      throw userError;
   });
}

function validateProductsExistOnShopify(products) {
   const nonExistentProducts = products.filter(product => !product.variantId);
   if (nonExistentProducts.length) {
      const joinedSkus = nonExistentProducts.map(product => product.sku).join(', ');
      Sentry.captureMessage("Some cart products don't exist on shopify", {
         extra: {
            'Non-existent products': nonExistentProducts,
         },
      });
      const rejectedPromise = Promise.reject(ITEM_PROBLEMS_PREFIX + joinedSkus);
      console.error(rejectedPromise);
      return rejectedPromise;
   }
}

function redirectWithSSO(returnToUrlString) {
   const returnToUrl = new URL(returnToUrlString);
   addUtmTrackerParam(returnToUrl);
   const ssoUrl = new URL(App.shopifyCheckout.ssoRoute);
   ssoUrl.searchParams.set('return_to', returnToUrl.toString());
   window.location = ssoUrl;
}

function addUtmTrackerParam(url) {
   const utmTrackers = window.localStorage?.getItem('utmTrackers');
   if (utmTrackers) {
      url.searchParams.set('utmTrackers', utmTrackers);
   }
}

function showLoadingIndicator(promise) {
   LoadingIndicator.withPromise(promise, {
      pendingMessage: _js('Creating checkout'),
      useErrorResponseAsFailureMessage: true,
      hideSuccessMessage: true,
      duration: 10000,
   });
}
